import api, { buildQueryString } from './api';
import type {
  Employee,
  EmployeeStatus,
  PaginatedResponse,
  ApiResponse,
  PaginationParams,
  CreateEmployeeForm,
} from '../types';

export interface EmployeeFilters extends PaginationParams {
  status?: EmployeeStatus;
  department?: string;
  managerId?: string;
}

export const employeeService = {
  async getAll(params?: EmployeeFilters): Promise<PaginatedResponse<Employee>> {
    const qs = buildQueryString(params as Record<string, unknown>);
    const response = await api.get<PaginatedResponse<Employee>>(`/api/employees${qs}`);
    return response.data;
  },

  async getById(id: string): Promise<Employee> {
    const response = await api.get<ApiResponse<Employee>>(`/api/employees/${id}`);
    return response.data.data;
  },

  async create(data: CreateEmployeeForm): Promise<Employee> {
    const response = await api.post<ApiResponse<Employee>>('/api/employees', data);
    return response.data.data;
  },

  async update(id: string, data: Partial<CreateEmployeeForm>): Promise<Employee> {
    const response = await api.patch<ApiResponse<Employee>>(`/api/employees/${id}`, data);
    return response.data.data;
  },

  async setPassword(id: string, password: string): Promise<void> {
    await api.patch(`/api/employees/${id}/password`, { password });
  },

  async updateStatus(id: string, status: EmployeeStatus): Promise<Employee> {
    const response = await api.patch<ApiResponse<Employee>>(`/api/employees/${id}/status`, { status });
    return response.data.data;
  },

  async delete(id: string): Promise<void> {
    await api.delete(`/api/employees/${id}`);
  },

  async getManagers(): Promise<Employee[]> {
    const response = await api.get<ApiResponse<Employee[]>>('/api/employees/managers');
    return response.data.data;
  },

  async getMyTeam(): Promise<Employee[]> {
    const response = await api.get<ApiResponse<Employee[]>>('/api/employees/my-team');
    return response.data.data;
  },

  async getLiveLocations(): Promise<Employee[]> {
    const response = await api.get<ApiResponse<Employee[]>>('/api/employees/live-locations');
    return response.data.data;
  },

  async getLocationHistory(id: string, date: string): Promise<Array<{
    latitude: number;
    longitude: number;
    timestamp: string;
    accuracy?: number;
  }>> {
    const response = await api.get<ApiResponse<Array<{
      latitude: number;
      longitude: number;
      timestamp: string;
      accuracy?: number;
    }>>>(`/api/employees/${id}/location-history?date=${date}`);
    return response.data.data;
  },
};
